/**
 * BrStatusPanel — Top-center HUD panel shown only in Battle Royale mode
 * during the 'playing' phase. Displays:
 *   - N REMAINING (count of non-eliminated players)
 *   - NEXT SHRINK M:SS countdown (or FINAL RING when no more shrinks)
 *   - Stage indicator (e.g. "Stage 2/3")
 */
import { BR_SHRINK_SCHEDULE, TRAINING_DUMMY_ID } from '../../shared/constants.js';
import { formatMMSS } from './format.js';

interface PlayerLike {
  eliminated?: boolean;
  sessionId?: string;
}

interface BrStateLike {
  mode?: string;
  phase?: string;
  elapsedTime?: number;
  nextShrinkAt?: number;
  shrinkStageIndex?: number;
  players?: Map<string, PlayerLike> | { forEach: (cb: (p: PlayerLike) => void) => void };
}

const TOTAL_STAGES = BR_SHRINK_SCHEDULE.length;

/**
 * Count non-eliminated, non-dummy players in a Colyseus players map.
 * Exported for unit testing — lives outside the DOM-coupled class.
 */
export function countRemaining(
  players:
    | { forEach: (cb: (p: PlayerLike) => void) => void }
    | null
    | undefined,
): number {
  if (!players) return 0;
  let remaining = 0;
  players.forEach((p) => {
    if (p?.sessionId === TRAINING_DUMMY_ID) return;
    if (!p?.eliminated) remaining++;
  });
  return remaining;
}

/**
 * Pure state → label transformer. Returns the strings the panel should
 * display. Exported for unit testing.
 */
export function brPanelLabels(state: BrStateLike | null | undefined): {
  remaining: string;
  timer: string;
  stage: string;
} {
  const remaining = countRemaining(state?.players);
  const stageIndex = state?.shrinkStageIndex ?? 0;
  const elapsed = state?.elapsedTime ?? 0;
  const nextShrinkAt = state?.nextShrinkAt ?? 0;
  const secondsLeft = Math.max(0, Math.ceil(nextShrinkAt - elapsed));

  if (stageIndex >= TOTAL_STAGES) {
    return {
      remaining: `${remaining} REMAINING`,
      timer: 'FINAL RING',
      stage: `Stage ${TOTAL_STAGES}/${TOTAL_STAGES}`,
    };
  }
  return {
    remaining: `${remaining} REMAINING`,
    timer: `NEXT SHRINK ${formatMMSS(secondsLeft)}`,
    stage: `Stage ${stageIndex + 1}/${TOTAL_STAGES}`,
  };
}

export class BrStatusPanel {
  private container: HTMLElement;
  private remainingEl: HTMLElement;
  private timerEl: HTMLElement;
  private stageEl: HTMLElement;
  private lastRemaining = '';
  private lastTimer = '';
  private lastStage = '';

  constructor(parent: HTMLElement) {
    this.container = document.createElement('div');
    this.container.className = 'hud-panel br-status hidden';

    const title = document.createElement('div');
    title.className = 'hud-panel-title';
    title.textContent = 'BATTLE ROYALE';
    this.container.appendChild(title);

    this.remainingEl = document.createElement('div');
    this.remainingEl.className = 'br-remaining';
    this.remainingEl.textContent = '0 REMAINING';
    this.container.appendChild(this.remainingEl);

    this.timerEl = document.createElement('div');
    this.timerEl.className = 'br-timer';
    this.timerEl.textContent = 'NEXT SHRINK --:--';
    this.container.appendChild(this.timerEl);

    this.stageEl = document.createElement('div');
    this.stageEl.className = 'br-stage';
    this.stageEl.textContent = `Stage 0/${TOTAL_STAGES}`;
    this.container.appendChild(this.stageEl);

    parent.appendChild(this.container);
  }

  show(): void {
    this.container.classList.remove('hidden');
  }

  hide(): void {
    this.container.classList.add('hidden');
  }

  update(state: BrStateLike | null | undefined): void {
    if (!state) return;
    const labels = brPanelLabels(state);
    if (labels.remaining !== this.lastRemaining) {
      this.remainingEl.textContent = labels.remaining;
      this.lastRemaining = labels.remaining;
    }
    if (labels.timer !== this.lastTimer) {
      this.timerEl.textContent = labels.timer;
      this.lastTimer = labels.timer;
    }
    if (labels.stage !== this.lastStage) {
      this.stageEl.textContent = labels.stage;
      this.lastStage = labels.stage;
    }
  }
}
